import { useState, useEffect } from 'react';
import { qcAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, XCircle, ClipboardCheck, Send } from 'lucide-react';

const QC_TYPE_LABELS = { fabric_receipt: 'Fabric Receipt', pre_trial: 'Pre-Trial', post_alteration: 'Post-Alteration' };
const RESULT_CFG = {
  pass: { label: 'Pass', bg: '#059669', icon: CheckCircle2 },
  flag: { label: 'Flag', bg: '#D97706', icon: AlertTriangle },
  fail: { label: 'Fail', bg: '#DC2626', icon: XCircle },
};

export default function QCInspectionForm({ open, onClose, qcType, garmentType, orderItemId, poLineItemId, onComplete }) {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setNotes('');
    qcAPI.getTemplate(garmentType || '_all', qcType).then(res => {
      const items = (res.data?.checklist_items || []).map(name => ({
        check_name: name,
        result: null, // not yet inspected
        notes: '',
      }));
      setChecklist(items);
    }).catch(() => {
      setChecklist([]);
    }).finally(() => setLoading(false));
  }, [open, garmentType, qcType]);

  const setItemResult = (idx, result) => {
    setChecklist(prev => prev.map((item, i) => i === idx ? { ...item, result: item.result === result ? null : result } : item));
  };

  const setItemNotes = (idx, value) => {
    setChecklist(prev => prev.map((item, i) => i === idx ? { ...item, notes: value } : item));
  };

  const overall = (() => {
    const results = checklist.filter(c => c.result).map(c => c.result);
    if (results.length === 0) return null;
    if (results.includes('fail')) return 'fail';
    if (results.includes('flag')) return 'flag';
    return 'pass';
  })();

  const allInspected = checklist.every(c => c.result !== null);

  const handleSubmit = async () => {
    if (!allInspected) {
      toast.error('Please inspect all items before submitting');
      return;
    }
    setSubmitting(true);
    try {
      await qcAPI.createInspection({
        type: qcType,
        order_item_id: orderItemId || null,
        po_line_item_id: poLineItemId || null,
        checklist: checklist.map(c => ({ check_name: c.check_name, result: c.result, notes: c.notes })),
        notes,
      });
      const resultLabel = RESULT_CFG[overall]?.label || overall;
      toast.success(`QC ${QC_TYPE_LABELS[qcType]} — ${resultLabel}`);
      onComplete?.(overall);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save inspection');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qc-inspection-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <ClipboardCheck className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            QC: {QC_TYPE_LABELS[qcType] || qcType}
            {garmentType && <Badge variant="outline" className="text-xs capitalize ml-2">{garmentType}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>Loading checklist...</p>
        ) : checklist.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>No checklist template found for this garment type.</p>
        ) : (
          <div className="space-y-2 py-2">
            {checklist.map((item, idx) => (
              <div key={idx} className="p-3 rounded-xl border" data-testid={`qc-item-${idx}`}
                style={{
                  borderColor: item.result === 'fail' ? 'rgba(220,53,69,0.3)' : item.result === 'flag' ? 'rgba(217,119,6,0.3)' : item.result === 'pass' ? 'rgba(5,150,105,0.2)' : 'rgba(27,42,74,0.08)',
                  backgroundColor: item.result === 'fail' ? 'rgba(220,53,69,0.04)' : item.result === 'flag' ? 'rgba(217,119,6,0.04)' : item.result === 'pass' ? 'rgba(5,150,105,0.03)' : 'transparent',
                }}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium flex-1" style={{ color: 'var(--navy-ink)' }}>{item.check_name}</p>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {['pass', 'flag', 'fail'].map(r => {
                      const cfg = RESULT_CFG[r];
                      const active = item.result === r;
                      return (
                        <button key={r} onClick={() => setItemResult(idx, r)}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold touch-manipulation"
                          style={{
                            backgroundColor: active ? cfg.bg : 'rgba(27,42,74,0.04)',
                            color: active ? '#fff' : cfg.bg,
                            border: `2px solid ${active ? cfg.bg : 'transparent'}`,
                            minWidth: '72px', justifyContent: 'center',
                            transition: 'all 120ms',
                          }}
                          data-testid={`qc-${r}-${idx}`}>
                          <cfg.icon className="w-3.5 h-3.5" /> {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {(item.result === 'flag' || item.result === 'fail') && (
                  <Textarea value={item.notes} onChange={(e) => setItemNotes(idx, e.target.value)}
                    placeholder={`Notes for ${item.result === 'fail' ? 'failure' : 'flag'}...`}
                    rows={1} className="mt-2 text-sm" data-testid={`qc-notes-${idx}`} />
                )}
              </div>
            ))}

            <Separator className="my-3" />

            {/* Overall result */}
            <div className="flex items-center justify-between p-3 rounded-xl" style={{
              backgroundColor: overall ? (RESULT_CFG[overall]?.bg + '12') : 'rgba(27,42,74,0.04)',
            }}>
              <span className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Overall Result</span>
              {overall ? (
                <Badge className="text-sm px-3 py-1 font-bold" data-testid="qc-overall-result"
                  style={{ backgroundColor: RESULT_CFG[overall].bg, color: '#fff' }}>
                  {RESULT_CFG[overall].label}
                </Badge>
              ) : (
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Inspect all items</span>
              )}
            </div>

            {/* Inspector notes */}
            <div>
              <label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Inspector Notes</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="General notes..." rows={2} className="mt-1"
                data-testid="qc-inspector-notes" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="qc-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !allInspected}
            className="gap-2" style={{ backgroundColor: overall === 'fail' ? '#DC2626' : overall === 'flag' ? '#D97706' : 'var(--brand-navy)' }}
            data-testid="qc-submit-btn">
            <Send className="w-4 h-4" /> {submitting ? 'Saving...' : `Submit ${overall ? RESULT_CFG[overall].label : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
